import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import {
    LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID,
    type LinearComponentPatternHistorySnapshot,
} from '../../pattern/LinearComponentPatternTypes';
import {isSerializablePlainData} from './SerializablePlainData';

//------ 线性零部件阵列撤销重做 开始------
/**
 * 线性零部件阵列历史 adapter ID。
 */
export const LINEAR_COMPONENT_PATTERN_HISTORY_ADAPTER_ID = 'history-domain:linear-component-pattern';

/**
 * 线性零部件阵列历史实体 ID。
 *
 * 设计说明：
 * 当前线性阵列 manager 是装配体文档下的唯一运行时宿主，所以历史层以单实体整组快照恢复，
 * 避免创建、编辑、删除分别维护多个 patch 时产生顺序和孤儿节点问题。
 */
export const LINEAR_COMPONENT_PATTERN_HISTORY_ENTITY_ID = LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID;

/**
 * 线性零部件阵列历史运行时存储。
 */
export interface LinearComponentPatternHistoryStore {
    /**
     * 恢复线性零部件阵列 manager 快照。
     *
     * @param snapshot manager 历史快照。
     */
    readonly restoreLinearComponentPatternSnapshot: (snapshot: LinearComponentPatternHistorySnapshot) => void;
}

/**
 * 判断对象是否为普通对象。
 *
 * @param value 待判断对象。
 * @returns 是否为普通对象。
 */
function isPlainObject(value: object): boolean {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

/**
 * 递归移除历史快照中的 `undefined` 字段。
 *
 * 设计说明：
 * 1. 线性阵列定义中存在很多可选字段，运行时对象克隆后容易保留 `undefined`；
 * 2. CAD 历史协议只允许可序列化 plain data，`undefined` 不能进入 undo / redo 快照；
 * 3. 这里不放宽校验，而是在 adapter 边界把快照重新归一化为可恢复主状态。
 *
 * @param value 待归一化值。
 * @returns 已移除 `undefined` 的值。
 */
function omitUndefinedDeep(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value
            .map((item) => omitUndefinedDeep(item))
            .filter((item) => item !== undefined);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .map(([key, item]) => [key, omitUndefinedDeep(item)] as const)
            .filter(([, item]) => item !== undefined),
    );
}

/**
 * 归一化线性零部件阵列历史快照。
 *
 * @param snapshot 来源快照。
 * @returns 可序列化快照。
 */
function normalizeLinearComponentPatternHistorySnapshot(
    snapshot: LinearComponentPatternHistorySnapshot,
): LinearComponentPatternHistorySnapshot {
    return omitUndefinedDeep(snapshot) as LinearComponentPatternHistorySnapshot;
}

/**
 * 归一化历史实体快照。
 *
 * @param snapshot 来源实体快照。
 * @returns 可序列化实体快照。
 */
function normalizeLinearComponentPatternEntitySnapshot(
    snapshot: HistoryEntitySnapshot<LinearComponentPatternHistorySnapshot>,
): HistoryEntitySnapshot<LinearComponentPatternHistorySnapshot> {
    if (!snapshot.exists || !snapshot.data) {
        return snapshot;
    }

    return {
        ...snapshot,
        data: normalizeLinearComponentPatternHistorySnapshot(snapshot.data),
    };
}

/**
 * 判断线性零部件阵列历史快照是否为 plain data。
 *
 * @param snapshot 历史快照。
 * @returns 是否为可序列化 plain data。
 */
export function isPlainLinearComponentPatternHistorySnapshot(
    snapshot: LinearComponentPatternHistorySnapshot,
): boolean {
    const normalizedSnapshot = normalizeLinearComponentPatternHistorySnapshot(snapshot);

    return typeof snapshot.managerId === 'string'
        && Array.isArray(snapshot.records)
        && isSerializablePlainData(normalizedSnapshot);
}

/**
 * 应用线性零部件阵列历史快照。
 *
 * @param input 补丁应用输入。
 */
function applyLinearComponentPatternSnapshot(
    input: HistoryPatchApplyInput<
        LinearComponentPatternHistorySnapshot,
        LinearComponentPatternHistoryStore
    >,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.restoreLinearComponentPatternSnapshot({
            managerId: input.patch.entityId,
            records: [],
        });

        return;
    }

    if (!snapshot.data) {
        throw new Error(`线性零部件阵列 ${input.patch.entityId} 缺少快照数据。`);
    }

    const normalizedSnapshot = normalizeLinearComponentPatternHistorySnapshot(snapshot.data);

    if (!isPlainLinearComponentPatternHistorySnapshot(normalizedSnapshot)) {
        throw new Error(`线性零部件阵列 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.restoreLinearComponentPatternSnapshot(normalizedSnapshot);
}

/**
 * 比较线性零部件阵列历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areLinearComponentPatternSnapshotsEqual(
    left: HistoryEntitySnapshot<LinearComponentPatternHistorySnapshot>,
    right: HistoryEntitySnapshot<LinearComponentPatternHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建线性零部件阵列历史 adapter。
 *
 * @returns 线性零部件阵列历史 adapter。
 */
export function createLinearComponentPatternHistoryAdapter(): HistoryDomainAdapter<
    LinearComponentPatternHistorySnapshot,
    LinearComponentPatternHistoryStore
> {
    return {
        adapterId: LINEAR_COMPONENT_PATTERN_HISTORY_ADAPTER_ID,
        kind: 'linear-component-pattern',
        applySnapshot: applyLinearComponentPatternSnapshot,
        areSnapshotsEqual: areLinearComponentPatternSnapshotsEqual as HistoryDomainAdapter<
            LinearComponentPatternHistorySnapshot,
            LinearComponentPatternHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建线性零部件阵列历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 线性零部件阵列历史补丁。
 */
export function createLinearComponentPatternHistoryPatch(
    patch: Omit<EntityPatch<LinearComponentPatternHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<LinearComponentPatternHistorySnapshot> {
    return {
        ...patch,
        before: normalizeLinearComponentPatternEntitySnapshot(patch.before),
        after: normalizeLinearComponentPatternEntitySnapshot(patch.after),
        entityKind: 'linear-component-pattern',
        adapterId: LINEAR_COMPONENT_PATTERN_HISTORY_ADAPTER_ID,
    };
}
//------ 线性零部件阵列撤销重做 结束------
